import {z} from 'zod'
import {TKey} from '@/translations/i18n'
import {ProjectSmall} from '@/features/projects/types'
import {Access} from '@/features/accesses/types'
import {MedicalReportType, MedicalReportTypeGroupKeyName, MedicalReportTypeKeyName} from '@/types'

export const PatientDetailsMedicalRecordUrlParams = z.object({
    id: z.string().min(1),
    medicalReportId: z.string().min(1).optional()
})

export const MedicalReportTypeGroup = z.object({
    groupKeyName: MedicalReportTypeGroupKeyName,
    groupName: z.string(),
    types: z.array(MedicalReportType)
})
export type MedicalReportTypeGroup = z.infer<typeof MedicalReportTypeGroup>

export const CreateMedicalReportSearchParams = z.object({
    projectId: z.string().optional(),
    medicalReportTypeId: z.string().optional(),
    medicalReportTypeKeyName: MedicalReportTypeKeyName.optional(),
    medicalReportTypeName: z.string().optional(),
    medicalReportTypeGroupKeyName: MedicalReportTypeGroupKeyName.optional(),
    medicalReportTypeGroupName: z.string().optional(),
    accessIds: z.string().array().optional(),
    date: z.string().optional()
})
export type CreateMedicalReportSearchParams = z.infer<typeof CreateMedicalReportSearchParams>

const medicalReportAttributeKeys = [
    'diagnosis',
    'clinicalUpdate',
    'pathologicalHistory',
    'remotePathologicalHistory',
    'familyHistory',
    'physiologicalHistory',
    'physicalExam',
    'socioFactors',
    'conclusions',
    'notes',
    'treatmentsPlan',
    'objectives',
    'cyclePeriodicityHypothesis',
    'closureReason',
    'reopeningNextCycle',
    'therapistEvaluation',
    'teamEvaluation',
    'medicalEvaluation',
    'newProjectStartDate',
    'newProjectDurationDays',
    'diagnosisNotes',
    'additionalPredefinedText1',
    'isAdditionalPredefinedText2',
    'additionalPredefinedText2',
    'additionalNotes',
    'treatments',
    'otherAccess',
    'evaluationObservation',
    'projectDoctorVisit',
    'cardiologyVisit',
    'pneumologyVisit',
    'orthopedicsVisit',
    'orlSwallowingVisit',
    'nutritionalVisit',
    'physiatryVisit',
    'neurologyVisit',
    'socialServiceIntervention',
    'therapistEvaluation',
    'psychologistEvaluation',
    'medicalInterview',
    'therapyObservation',
    'speechTherapy',
    'respiratoryRehabilitation',
    'neuroMotorRehabilitation',
    'psychomotorRehabilitation',
    'cognitiveRehabilitation',
    'psychologicalTherapy',
    'occupationalTherapy',
    'educationalIntervention',
    'counseling',
    'teamMeetings',
    'qualification',
    'workingCondition',
    'jobType',
    'civilDisability',
    'companionAllowance',
    'attendanceAllowance',
    'law104Article3Paragraph1',
    'law104Article3Paragraph3',
    'disabilityPercentage',
    'companionAllowanceReviewable',
    'attendanceAllowanceReviewable',
    'houseType',
    'houseTypeOther',
    'hasArchitecturalBarriers',
    'architecturalBarriers',
    'serviceDislocation',
    'otherHousingNotes',
    'referenceContactFirstName',
    'referenceContactLastName',
    'referenceKinship',
    'referencePhone',
    'familyMembersSituations',
    'intraFamilyCommunicationDifficulty',
    'extraFamilyCommunicationDifficulty',
    'disabilityExperienceProcessing',
    'volunteerNetwork',
    'volunteerNetworkDetails',
    'paidNetwork',
    'paidNetworkDetails',
    'activatedTerritorialServices',
    'municipalTerritorialServices',
    'aslTerritorialServices',
    'homeCare',
    'homeCareNotes',
    'transport',
    'transportNotes',
    'dayCenters',
    'dayCentersNotes',
    'associations',
    'associationsNotes',
    'otherServices',
    'otherServicesNotes',
    'hobbiesNotes',
    'schoolType',
    'schoolName',
    'hasSupportTeacher',
    'supportTeacherHours',
    'hasOepa',
    'oepaHours',
    'hasCommunicationAssistant',
    'inclusionLevel',
    'familySituationMinors',
    'plannedAccesses',
    'familySituationMinors',
    'teamMembers',
    'documentTitle',
    'documentDate',
    'documentAttachment'
] as const
export const MedicalReportAttributeKey = z.enum(medicalReportAttributeKeys)
export type MedicalReportAttributeKey = z.infer<typeof MedicalReportAttributeKey>

export const MedicalReportProject = ProjectSmall.pick({
    id: true,
    code: true,
    startDate: true,
    expirationDate: true,
    endDate: true,
    assistiveRegime: true,
    status: true,
    durationDays: true
})
export type MedicalReportProject = z.infer<typeof MedicalReportProject>

export const MedicalReportSmall = z.object({
    id: z.string().min(1),
    project: MedicalReportProject.nullish(),
    status: z.enum(['draft', 'signed']),
    date: z.string(),
    signedAt: z.string().nullable(),
    createdByFullName: z.string().nullable(),
    specializationName: z.string().nullable(),
    medicalRecordReportType: MedicalReportType,
    pdfGenerationStartedAt: z.string().nullable(),
    version: z.number().nullish(), // TODO: remove nullish after migration
    pdfAttachment: z
        .object({
            url: z.string().url().nullish()
        })
        .nullish()
})
export type MedicalReportSmall = z.infer<typeof MedicalReportSmall>

export const MedicalReportExtended = MedicalReportSmall.and(
    z.object({
        accesses: Access.array().nullish(),
        medicalRecordReportAttributes: z.array(
            z.object({
                id: z.string().min(1),
                name: MedicalReportAttributeKey,
                value: z.string(),
                object: z.record(z.string(), z.unknown()).array().nullish()
            })
        )
    })
)
export type MedicalReportExtended = z.infer<typeof MedicalReportExtended>

export type MedicalReportSectionCopy = {
    title: TKey
    description?: TKey
}

export const MedicalReportSearchParams = z.object({
    projectId: z.string().min(1),
    accessType: z.string().min(1)
})
export type MedicalReportSearchParams = z.infer<typeof MedicalReportSearchParams>

const ClosingReason = z.enum([
    'achievement_of_objectives',
    'patient_decision',
    'transition_to_territorial_services',
    'transfer_to_another_center_or_facility',
    'transfer_to_city_region_or_another_state',
    'therapist_shortage',
    'organizational_or_scheduling_difficulties',
    'lack_of_authorization_from_asl',
    'patient_death'
])
export type ClosingReason = z.infer<typeof ClosingReason>

export const CyclePeriodicityHypothesis = z.enum(['once_year', 'twice_year', 'single_non_repeatable_cycle'])
export type CyclePeriodicityHypothesis = z.infer<typeof CyclePeriodicityHypothesis>

export const PredefinedText = z.enum(['text_1', 'text_2', 'text_3'])
export type PredefinedText = z.infer<typeof PredefinedText>

export type MedicalReportView = 'create' | 'edit'

export const ProjectMedicalReport = z.object({
    medicalRecordReportAttributes: z
        .array(
            z.object({
                id: z.string(),
                name: MedicalReportAttributeKey,
                value: z.string()
            })
        )
        .optional()
})
export type ProjectMedicalReport = z.infer<typeof ProjectMedicalReport>

export const MedicalReportQualification = z.enum([
    'none',
    'elementary_license',
    'lower_secondary_license',
    'upper_secondary_school',
    'degree_or_higher'
])
export type MedicalReportQualification = z.infer<typeof MedicalReportQualification>

export const MedicalReportWorkingCondition = z.enum([
    'student',
    'unemployed',
    'jobless',
    'temporary_laid_off',
    'employed_permanent_contract',
    'employed_collaboration_contract',
    'employed_other',
    'retired_seniority_pension',
    'retired_social_pension',
    'retired_disability_pension',
    'retired_other'
])
export type MedicalReportWorkingCondition = z.infer<typeof MedicalReportWorkingCondition>

export const MedicalReportJobType = z.enum(['manual', 'knowledge_work'])
export type MedicalReportJobType = z.infer<typeof MedicalReportJobType>

export const MedicalReportHouseType = z.enum([
    'owned_home',
    'rented_home',
    'public_housing_assignment',
    'occupied_home',
    'eviction_condition',
    'free_use_loan',
    'other',
    'enter_text'
])
export type MedicalReportHouseType = z.infer<typeof MedicalReportHouseType>

export const DisabilityExperienceProcessing = z.enum([
    'processing_none',
    'processing_low',
    'processing_medium',
    'processing_good'
])
export type DisabilityExperienceProcessing = z.infer<typeof DisabilityExperienceProcessing>

export const SchoolType = z.enum([
    'none',
    'nursery',
    'kindergarten',
    'primary',
    'middle',
    'high',
    'training',
    'socio_work'
])
export type SchoolType = z.infer<typeof SchoolType>

export const InclusionLevel = z.enum(['low', 'medium', 'good', 'excellent'])
export type InclusionLevel = z.infer<typeof InclusionLevel>

export const FamilySituation = z.enum([
    'single_parent_working',
    'unemployed_parents',
    'part_time_work',
    'full_time_work',
    'siblings',
    'siblings_with_social_services',
    'single_parent_family',
    'separated_parents',
    'conflictual_separated_parents',
    'legal_guardian',
    'social_services_guardianship',
    'precarious_economic_conditions'
])
export type FamilySituation = z.infer<typeof FamilySituation>

export const MedicalReportTypeByProject = MedicalReportType.pick({
    id: true,
    name: true,
    keyName: true
})
export type MedicalReportTypeByProject = z.infer<typeof MedicalReportTypeByProject>

export const MedicalReportDateByProject = z.object({
    date: z.string()
})
export type MedicalReportDateByProject = z.infer<typeof MedicalReportDateByProject>
